import { 
  AITradingEngine, 
  PredictionResult, 
  ArbitrageOpportunity, 
  Asset, 
  RebalanceRecommendation, 
  Transaction, 
  RiskScore, 
  SentimentAnalysis,
  TradingSignal,
  YieldOpportunity,
  MarketPattern 
} from '../types/ai-trading';
import { handleAPIError, safeExecute, validateInput } from '../utils/error-handling';
import { safeGetEnv } from '../utils/env';

export class AITradingService implements AITradingEngine {
  private apiBaseUrl = 'https://api.coingecko.com/api/v3';
  private _newsApiKey: string | undefined = undefined;
  private _openaiApiKey: string | undefined = undefined;
  private _initialized = false;

  private ensureInitialized() {
    if (!this._initialized) {
      this._newsApiKey = safeGetEnv('REACT_APP_NEWS_API_KEY');
      this._openaiApiKey = safeGetEnv('REACT_APP_OPENAI_API_KEY');
      this._initialized = true;
    }
  }

  private get newsApiKey(): string | undefined {
    this.ensureInitialized();
    return this._newsApiKey;
  }

  private get openaiApiKey(): string | undefined {
    this.ensureInitialized();
    return this._openaiApiKey;
  }

  async getPricePrediction(token: string, timeframe: string): Promise<PredictionResult> {
    try {
      // Obtener datos históricos
      const historicalData = await this.getHistoricalPrices(token, timeframe);
      if (!historicalData || historicalData.length === 0) {
        throw new Error('No historical data available');
      }
      
      const currentPrice = await this.getCurrentPrice(token);
      if (!currentPrice || currentPrice <= 0) {
        throw new Error('Invalid current price data');
      }
      
      // Análisis técnico
      const technicalIndicators = this.calculateTechnicalIndicators(historicalData);
      
      // Análisis de sentimiento
      const sentiment = await this.getMarketSentiment(token);
      
      // Modelo predictivo simplificado (en producción usaríamos TensorFlow.js)
      const prediction = this.generatePricePrediction(
        currentPrice,
        technicalIndicators,
        sentiment,
        timeframe
      );

      return {
        token,
        currentPrice,
        predictedPrice: prediction.price,
        timeframe,
        confidence: prediction.confidence,
        direction: prediction.direction,
        keyFactors: prediction.factors,
        riskLevel: prediction.risk,
        timestamp: new Date()
      };
    } catch (error) {
      console.error('Error generating price prediction:', error);
      
      // Return fallback prediction instead of throwing
      return {
        token,
        currentPrice: 1000,
        predictedPrice: 1050,
        timeframe,
        confidence: 0.5,
        direction: 'bullish' as const,
        keyFactors: ['Technical analysis pending', 'Limited data available'],
        riskLevel: 'medium' as const,
        timestamp: new Date()
      };
    }
  }

  async getArbitrageOpportunities(): Promise<ArbitrageOpportunity[]> {
    try {
      // Simular búsqueda de arbitraje entre diferentes DEXs y chains
      const opportunities: ArbitrageOpportunity[] = [];
      
      const tokens = ['ETH', 'BTC', 'USDC', 'LINK', 'UNI'];
      const chains = ['ethereum', 'bsc', 'polygon'];
      
      for (const token of tokens) {
        for (let i = 0; i < chains.length; i++) {
          for (let j = i + 1; j < chains.length; j++) {
            const priceA = await this.getPriceOnChain(token, chains[i]);
            const priceB = await this.getPriceOnChain(token, chains[j]);
            
            const spread = Math.abs(priceA - priceB) / Math.min(priceA, priceB) * 100;
            
            if (spread > 0.5) { // Mínimo 0.5% spread
              opportunities.push({
                id: `${token}-${chains[i]}-${chains[j]}`,
                tokenA: token,
                tokenB: token,
                chainA: chains[i],
                chainB: chains[j],
                priceA,
                priceB,
                spreadPercentage: spread,
                estimatedProfit: spread * 1000, // Ejemplo con $1000
                gasEstimate: this.estimateGasCost(chains[i], chains[j]),
                executionTime: this.estimateExecutionTime(chains[i], chains[j]),
                riskScore: this.calculateArbitrageRisk(spread, chains[i], chains[j]),
                liquidityA: Math.random() * 10000000,
                liquidityB: Math.random() * 10000000
              });
            }
          }
        }
      }

      return opportunities.sort((a, b) => b.estimatedProfit - a.estimatedProfit).slice(0, 10);
    } catch (error) {
      console.error('Error finding arbitrage opportunities:', error);
      return [];
    }
  }

  async optimizePortfolio(currentHoldings: Asset[]): Promise<RebalanceRecommendation> {
    try {
      // Análisis de correlación entre assets
      const correlationMatrix = await this.calculateCorrelationMatrix(currentHoldings);
      
      // Cálculo de volatilidad y retorno esperado
      const riskReturnData = await this.calculateRiskReturn(currentHoldings);
      
      // Optimización usando teoría moderna de portfolios
      const optimizedAllocation = this.optimizeAllocation(correlationMatrix, riskReturnData);
      
      const currentAllocation: Record<string, number> = {};
      const totalValue = currentHoldings.reduce((sum, asset) => sum + asset.usdValue, 0);
      
      currentHoldings.forEach(asset => {
        currentAllocation[asset.symbol] = (asset.usdValue / totalValue) * 100;
      });

      const actions = this.generateRebalanceActions(currentAllocation, optimizedAllocation, totalValue);

      return {
        currentAllocation,
        recommendedAllocation: optimizedAllocation,
        actions,
        expectedReturn: this.calculateExpectedReturn(optimizedAllocation, riskReturnData),
        riskReduction: this.calculateRiskReduction(currentAllocation, optimizedAllocation, correlationMatrix),
        gasCost: actions.reduce((sum, action) => sum + 50, 0), // Estimado
        reasoning: this.generateOptimizationReasoning(actions, optimizedAllocation)
      };
    } catch (error) {
      console.error('Error optimizing portfolio:', error);
      throw error;
    }
  }

  async assessRisk(transaction: Transaction): Promise<RiskScore> {
    try {
      const tokenData = await this.getTokenData(transaction.token);
      
      const riskComponents = {
        volatility: this.calculateVolatilityRisk(tokenData),
        liquidity: this.calculateLiquidityRisk(tokenData),
        smart_contract: await this.assessSmartContractRisk(transaction.to),
        market_cap: this.calculateMarketCapRisk(tokenData.market_cap),
        regulatory: this.assessRegulatoryRisk(transaction.token)
      };

      const overall = Object.values(riskComponents).reduce((sum, risk) => sum + risk, 0) / 5;
      
      const flags = this.generateRiskFlags(riskComponents, transaction);
      
      let recommendation: 'proceed' | 'caution' | 'avoid';
      if (overall < 30) recommendation = 'proceed';
      else if (overall < 70) recommendation = 'caution';
      else recommendation = 'avoid';

      return {
        overall,
        components: riskComponents,
        flags,
        recommendation,
        reasoning: this.generateRiskReasoning(riskComponents, overall)
      };
    } catch (error) {
      console.error('Error assessing risk:', error);
      throw error;
    }
  }

  async getMarketSentiment(token: string): Promise<SentimentAnalysis> {
    try {
      // Análisis de redes sociales (simulado) - with fallback
      const socialSentiment = await this.analyzeSocialSentiment(token).catch(() => 0);
      
      // Análisis de noticias - with fallback
      const newsSentiment = await this.analyzeNewsSentiment(token).catch(() => 0);
      
      // Análisis técnico - with fallback
      const technicalSentiment = this.analyzeTechnicalSentiment(token) || 0;
      
      // Análisis on-chain - with fallback
      const onchainSentiment = await this.analyzeOnchainMetrics(token).catch(() => 0);
      
      const overall = (socialSentiment + newsSentiment + technicalSentiment + onchainSentiment) / 4;

      return {
        token,
        overall,
        sources: {
          social: socialSentiment,
          news: newsSentiment,
          technical: technicalSentiment,
          onchain: onchainSentiment
        },
        keywords: await this.extractTrendingKeywords(token),
        volume: Math.random() * 1000000,
        trends: {
          hourly: Array.from({ length: 24 }, () => Math.random() * 2 - 1),
          daily: Array.from({ length: 7 }, () => Math.random() * 2 - 1)
        },
        alerts: this.generateSentimentAlerts(overall, token)
      };
    } catch (error) {
      console.error('Error analyzing market sentiment:', error);
      
      // Return fallback sentiment data instead of throwing
      return {
        token,
        overall: 0,
        sources: {
          social: 0,
          news: 0,
          technical: 0,
          onchain: 0
        },
        keywords: ['market', 'analysis', 'pending'],
        confidence: 0.3,
        alerts: ['Sentiment analysis temporarily unavailable']
      };
    }
  }

  // Métodos auxiliares
  private async getHistoricalPrices(token: string, timeframe: string): Promise<number[]> {
    try {
      let days = 30;
      if (timeframe.includes('hour')) days = 1;
      else if (timeframe.includes('week')) days = 7;
      else if (timeframe.includes('month')) days = 30;
      
      // For demo purposes, generate realistic mock historical data
      // In production, this would go through a backend proxy
      const currentPrice = await this.getCurrentPrice(token);
      const dataPoints = Math.min(days * 24, 100); // Limit data points for performance
      
      const historicalPrices: number[] = [];
      
      // Generate realistic price movement with volatility based on token
      const volatility = this.getTokenVolatility(token);
      let price = currentPrice * (0.85 + Math.random() * 0.3); // Start with varied base
      
      for (let i = 0; i < dataPoints; i++) {
        // Random walk with market-like behavior
        const change = (Math.random() - 0.5) * volatility;
        price = Math.max(price * (1 + change), 0.01); // Prevent negative prices
        historicalPrices.push(Math.round(price * 100) / 100);
      }
      
      // Simulate network delay
      await new Promise(resolve => setTimeout(resolve, 100 + Math.random() * 100));
      
      return historicalPrices;
    } catch (error) {
      console.error('Error fetching historical prices:', error);
      // Return fallback data with at least some values
      const fallbackPrice = 1000;
      return Array.from({ length: 30 }, (_, i) => 
        fallbackPrice * (0.9 + (i / 30) * 0.2 + (Math.random() - 0.5) * 0.1)
      );
    }
  }

  private getTokenVolatility(token: string): number {
    // Different tokens have different volatility profiles
    const volatilities: Record<string, number> = {
      'bitcoin': 0.03,
      'ethereum': 0.04,
      'chainlink': 0.06,
      'uniswap': 0.08,
      'aave': 0.07,
      'btc': 0.03,
      'eth': 0.04,
      'link': 0.06,
      'uni': 0.08,
      'matic': 0.05,
      'bnb': 0.04
    };
    
    return volatilities[token.toLowerCase()] || 0.05; // Default 5% volatility
  }

  private async getCurrentPrice(token: string): Promise<number> {
    try {
      // For demo purposes, use mock data to avoid CORS issues
      // In production, this would go through a backend proxy
      const mockPrices: Record<string, number> = {
        'ethereum': 3240.50,
        'bitcoin': 67850.00,
        'chainlink': 14.75,
        'uniswap': 8.32,
        'aave': 156.90,
        'eth': 3240.50,
        'btc': 67850.00,
        'link': 14.75,
        'uni': 8.32,
        'matic': 0.87,
        'bnb': 605.20
      };

      // Add some random variation to simulate real price movement
      const basePrice = mockPrices[token.toLowerCase()] || mockPrices['ethereum'];
      const variation = (Math.random() - 0.5) * 0.1; // ±5% variation
      const currentPrice = basePrice * (1 + variation);
      
      // Simulate network delay
      await new Promise(resolve => setTimeout(resolve, 100 + Math.random() * 200));
      
      const finalPrice = Math.round(currentPrice * 100) / 100; // Round to 2 decimal places
      console.log(`AI Trading: Generated mock price for ${token}: ${finalPrice}`);
      return finalPrice;
    } catch (error) {
      console.error('Error fetching current price:', error);
      // Return fallback price for the token
      const fallbackPrices: Record<string, number> = {
        'ethereum': 3200,
        'bitcoin': 67000,
        'chainlink': 14,
        'uniswap': 8,
        'aave': 150
      };
      return fallbackPrices[token.toLowerCase()] || 1000;
    }
  }

  private calculateTechnicalIndicators(prices: number[]) {
    const sma20 = this.calculateSMA(prices, 20);
    const sma50 = this.calculateSMA(prices, 50);
    const rsi = this.calculateRSI(prices);
    const macd = this.calculateMACD(prices);
    
    return { sma20, sma50, rsi, macd };
  }

  private calculateSMA(prices: number[], period: number): number {
    if (prices.length < period) return prices[prices.length - 1] || 0;
    const slice = prices.slice(-period);
    return slice.reduce((sum, price) => sum + price, 0) / period;
  }

  private calculateRSI(prices: number[], period: number = 14): number {
    if (prices.length < period + 1) return 50;
    
    let gains = 0;
    let losses = 0;
    
    for (let i = 1; i <= period; i++) {
      const change = prices[prices.length - i] - prices[prices.length - i - 1];
      if (change > 0) gains += change;
      else losses -= change;
    }
    
    const avgGain = gains / period;
    const avgLoss = losses / period;
    const rs = avgGain / avgLoss;
    
    return 100 - (100 / (1 + rs));
  }

  private calculateMACD(prices: number[]) {
    const ema12 = this.calculateEMA(prices, 12);
    const ema26 = this.calculateEMA(prices, 26);
    return ema12 - ema26;
  }

  private calculateEMA(prices: number[], period: number): number {
    if (prices.length === 0) return 0;
    if (prices.length === 1) return prices[0];
    
    const multiplier = 2 / (period + 1);
    let ema = prices[0];
    
    for (let i = 1; i < prices.length; i++) {
      ema = (prices[i] * multiplier) + (ema * (1 - multiplier));
    }
    
    return ema;
  }

  private generatePricePrediction(
    currentPrice: number,
    technicalIndicators: any,
    sentiment: SentimentAnalysis,
    timeframe: string
  ) {
    // Algoritmo simplificado de predicción
    let prediction = currentPrice;
    let confidence = 0.5;
    
    // Factores técnicos
    if (technicalIndicators.rsi > 70) {
      prediction *= 0.95; // Sobreventa
      confidence += 0.1;
    } else if (technicalIndicators.rsi < 30) {
      prediction *= 1.05; // Sobrecompra
      confidence += 0.1;
    }
    
    // Factor de sentimiento
    prediction *= (1 + sentiment.overall * 0.1);
    confidence += Math.abs(sentiment.overall) * 0.2;
    
    // Factor temporal
    const timeMultiplier = timeframe.includes('hour') ? 0.02 : 
                          timeframe.includes('day') ? 0.05 : 0.1;
    
    const volatility = Math.random() * timeMultiplier;
    prediction *= (1 + (Math.random() - 0.5) * volatility);
    
    const direction = prediction > currentPrice ? 'bullish' : 
                     prediction < currentPrice ? 'bearish' : 'neutral';
    
    const priceChange = Math.abs(prediction - currentPrice) / currentPrice;
    const risk = priceChange > 0.1 ? 'high' : priceChange > 0.05 ? 'medium' : 'low';
    
    return {
      price: prediction,
      confidence: Math.min(confidence, 0.95),
      direction: direction as 'bullish' | 'bearish' | 'neutral',
      factors: this.generatePredictionFactors(technicalIndicators, sentiment),
      risk: risk as 'low' | 'medium' | 'high'
    };
  }

  private generatePredictionFactors(technicalIndicators: any, sentiment: SentimentAnalysis): string[] {
    const factors = [];
    
    if (technicalIndicators.rsi > 70) factors.push('RSI indica sobreventa');
    if (technicalIndicators.rsi < 30) factors.push('RSI indica sobrecompra');
    if (sentiment.overall > 0.3) factors.push('Sentimiento positivo del mercado');
    if (sentiment.overall < -0.3) factors.push('Sentimiento negativo del mercado');
    if (technicalIndicators.macd > 0) factors.push('MACD muestra momento alcista');
    
    return factors;
  }

  private async getPriceOnChain(token: string, chain: string): Promise<number> {
    // Simular precios ligeramente diferentes entre chains
    const basePrice = await this.getCurrentPrice(token);
    const variance = (Math.random() - 0.5) * 0.02; // ±1% variance
    return basePrice * (1 + variance);
  }

  private estimateGasCost(chainA: string, chainB: string): number {
    const gasCosts = {
      ethereum: 100,
      bsc: 5,
      polygon: 1
    };
    return (gasCosts[chainA as keyof typeof gasCosts] || 50) + 
           (gasCosts[chainB as keyof typeof gasCosts] || 50);
  }

  private estimateExecutionTime(chainA: string, chainB: string): number {
    // Tiempo en segundos
    const blockTimes = {
      ethereum: 12,
      bsc: 3,
      polygon: 2
    };
    return (blockTimes[chainA as keyof typeof blockTimes] || 10) * 2;
  }

  private calculateArbitrageRisk(spread: number, chainA: string, chainB: string): number {
    let risk = 10; // Base risk
    if (spread > 2) risk += 20; // High spread = higher risk
    if (chainA === 'ethereum' || chainB === 'ethereum') risk += 30; // ETH gas risk
    return Math.min(risk, 100);
  }

  private async calculateCorrelationMatrix(assets: Asset[]): Promise<number[][]> {
    // Simulación de matriz de correlación
    const matrix: number[][] = [];
    for (let i = 0; i < assets.length; i++) {
      matrix[i] = [];
      for (let j = 0; j < assets.length; j++) {
        if (i === j) matrix[i][j] = 1;
        else matrix[i][j] = Math.random() * 0.8 - 0.4; // -0.4 to 0.4
      }
    }
    return matrix;
  }

  private async calculateRiskReturn(assets: Asset[]): Promise<Array<{return: number, risk: number}>> {
    return assets.map(asset => ({
      return: (Math.random() * 0.3) + 0.05, // 5% to 35% return
      risk: Math.random() * 0.5 + 0.1 // 10% to 60% volatility
    }));
  }

  private optimizeAllocation(
    correlationMatrix: number[][],
    riskReturnData: Array<{return: number, risk: number}>
  ): Record<string, number> {
    // Algoritmo simplificado de optimización de Markowitz
    const numAssets = riskReturnData.length;
    const allocation: Record<string, number> = {};
    
    // Weights basados en ratio retorno/riesgo
    const weights = riskReturnData.map(data => data.return / data.risk);
    const totalWeight = weights.reduce((sum, w) => sum + w, 0);
    
    weights.forEach((weight, index) => {
      allocation[`Asset${index}`] = (weight / totalWeight) * 100;
    });
    
    return allocation;
  }

  private generateRebalanceActions(
    current: Record<string, number>,
    target: Record<string, number>,
    totalValue: number
  ) {
    const actions = [];
    
    for (const [asset, targetPercent] of Object.entries(target)) {
      const currentPercent = current[asset] || 0;
      const difference = targetPercent - currentPercent;
      
      if (Math.abs(difference) > 1) { // Only rebalance if >1% difference
        actions.push({
          type: difference > 0 ? 'buy' as const : 'sell' as const,
          asset,
          amount: Math.abs(difference),
          usdValue: Math.abs(difference) * totalValue / 100,
          priority: Math.abs(difference) > 5 ? 1 : 2
        });
      }
    }
    
    return actions.sort((a, b) => a.priority - b.priority);
  }

  private calculateExpectedReturn(
    allocation: Record<string, number>,
    riskReturnData: Array<{return: number, risk: number}>
  ): number {
    const values = Object.values(allocation);
    return values.reduce((sum, weight, index) => {
      return sum + (weight / 100) * (riskReturnData[index]?.return || 0);
    }, 0) * 100;
  }

  private calculateRiskReduction(
    current: Record<string, number>,
    optimized: Record<string, number>,
    correlationMatrix: number[][]
  ): number {
    // Cálculo simplificado de reducción de riesgo
    return Math.random() * 20 + 5; // 5-25% risk reduction
  }

  private generateOptimizationReasoning(actions: any[], allocation: Record<string, number>): string {
    if (actions.length === 0) return "Tu portfolio ya está bien optimizado";
    
    let reasoning = "Recomendamos los siguientes ajustes para mejorar el ratio riesgo-retorno: ";
    reasoning += actions.slice(0, 3).map(action => 
      `${action.type === 'buy' ? 'Aumentar' : 'Reducir'} posición en ${action.asset}`
    ).join(', ');
    
    return reasoning;
  }

  private async getTokenData(token: string) {
    try {
      // For demo purposes, return mock token data to avoid CORS issues
      // In production, this would go through a backend proxy
      const mockTokenData: Record<string, any> = {
        'ethereum': {
          market_cap: { usd: 390000000000 },
          total_volume: { usd: 15000000000 },
          market_cap_rank: 2,
          price_change_percentage_24h: 2.34
        },
        'bitcoin': {
          market_cap: { usd: 1340000000000 },
          total_volume: { usd: 28000000000 },
          market_cap_rank: 1,
          price_change_percentage_24h: 1.87
        },
        'chainlink': {
          market_cap: { usd: 8500000000 },
          total_volume: { usd: 450000000 },
          market_cap_rank: 15,
          price_change_percentage_24h: -1.23
        },
        'uniswap': {
          market_cap: { usd: 5200000000 },
          total_volume: { usd: 180000000 },
          market_cap_rank: 23,
          price_change_percentage_24h: 3.45
        },
        'aave': {
          market_cap: { usd: 2100000000 },
          total_volume: { usd: 85000000 },
          market_cap_rank: 47,
          price_change_percentage_24h: 0.98
        }
      };

      const tokenKey = token.toLowerCase();
      const data = mockTokenData[tokenKey] || mockTokenData['ethereum'];
      
      // Add some random variation to simulate real market changes
      const variation = (Math.random() - 0.5) * 0.1;
      return {
        ...data,
        market_cap: { usd: data.market_cap.usd * (1 + variation * 0.05) },
        total_volume: { usd: data.total_volume.usd * (1 + variation * 0.2) },
        price_change_percentage_24h: data.price_change_percentage_24h + variation * 5
      };
    } catch (error) {
      console.error('Error fetching token data:', error);
      return { 
        market_cap: { usd: 1000000000 }, 
        total_volume: { usd: 100000000 },
        market_cap_rank: 50,
        price_change_percentage_24h: 0
      };
    }
  }

  private calculateVolatilityRisk(tokenData: any): number {
    // Simulación basada en volumen y market cap
    const volumeRatio = tokenData.total_volume?.usd / tokenData.market_cap?.usd || 0.1;
    return Math.min(volumeRatio * 100, 100);
  }

  private calculateLiquidityRisk(tokenData: any): number {
    const marketCap = tokenData.market_cap?.usd || 0;
    if (marketCap > 1e9) return 10; // Large cap
    if (marketCap > 1e8) return 30; // Mid cap
    return 70; // Small cap
  }

  private async assessSmartContractRisk(contractAddress: string): Promise<number> {
    // En producción, esto verificaría auditorías, código, etc.
    return Math.random() * 60 + 10; // 10-70%
  }

  private calculateMarketCapRisk(marketCap: number): number {
    if (marketCap > 1e10) return 10; // Very large
    if (marketCap > 1e9) return 25;  // Large
    if (marketCap > 1e8) return 45;  // Medium
    return 80; // Small
  }

  private assessRegulatoryRisk(token: string): number {
    const riskTokens = ['privacy', 'anonymous', 'mixer'];
    const isHighRisk = riskTokens.some(risk => token.toLowerCase().includes(risk));
    return isHighRisk ? 80 : Math.random() * 30 + 10;
  }

  private generateRiskFlags(components: any, transaction: Transaction): string[] {
    const flags = [];
    
    if (components.volatility > 60) flags.push('High volatility detected');
    if (components.smart_contract > 70) flags.push('Smart contract risks identified');
    if (components.liquidity > 60) flags.push('Low liquidity warning');
    if (transaction.gasPrice > 100) flags.push('High gas price');
    
    return flags;
  }

  private generateRiskReasoning(components: any, overall: number): string {
    const highest = Object.entries(components).reduce((a, b) => 
      components[a[0]] > components[b[0]] ? a : b
    );
    
    return `Risk assessment: ${overall.toFixed(0)}/100. Primary concern: ${highest[0]} (${components[highest[0]].toFixed(0)}/100)`;
  }

  private async analyzeSocialSentiment(token: string): Promise<number> {
    // En producción, esto analizaría Twitter, Reddit, etc.
    return Math.random() * 2 - 1; // -1 to 1
  }

  private async analyzeNewsSentiment(token: string): Promise<number> {
    // En producción, esto usaría APIs de noticias + NLP
    return Math.random() * 2 - 1;
  }

  private analyzeTechnicalSentiment(token: string): number {
    // Basado en indicadores técnicos
    return Math.random() * 2 - 1;
  }

  private async analyzeOnchainMetrics(token: string): Promise<number> {
    // Análisis de métricas on-chain (volumen, holders, etc.)
    return Math.random() * 2 - 1;
  }

  private async extractTrendingKeywords(token: string): Promise<string[]> {
    return ['bullish', 'adoption', 'partnership', 'upgrade', 'listing'];
  }

  private generateSentimentAlerts(overall: number, token: string): string[] {
    const alerts = [];
    
    if (overall > 0.7) alerts.push(`Strong positive sentiment for ${token}`);
    if (overall < -0.7) alerts.push(`Strong negative sentiment for ${token}`);
    if (Math.abs(overall) > 0.8) alerts.push('Extreme sentiment detected - exercise caution');
    
    return alerts;
  }
}

// Lazy initialization to avoid module loading errors
let _aiTradingService: AITradingService | null = null;

export const getAITradingService = (): AITradingService => {
  if (!_aiTradingService) {
    _aiTradingService = new AITradingService();
  }
  return _aiTradingService;
};